import { useEffect, useRef } from "react";
import { classNames } from "primereact/utils";
import { useCashdeskPage } from "./hooks/useCashdeskPage";
import { useCashdeskSale } from "./hooks/useCashdeskSale";
import { useCashdeskOrders } from "./hooks/useCashdeskOrders";
import { CashdeskSaleTab } from "./sale/CashdeskSaleTab";
import { CashdeskOrdersTab } from "./orders/CashdeskOrdersTab";
import { OnboardingItemKey } from "../../common/enums";
import { useOnboarding } from "../onboarding/OnboardingProvider";
import styles from "./CashdeskPage.module.scss";

export const CashdeskPage = () => {
  const {
    activeTab,
    setActiveTab,
    studioSettings,
    categorizedData,
    isProductsLoading,
  } = useCashdeskPage();

  const sale = useCashdeskSale({ studioSettings });
  const orders = useCashdeskOrders();

  const onboarding = useOnboarding();
  const hasAutoActivatedRef = useRef(false);

  const hasProducts =
    !isProductsLoading &&
    (categorizedData?.items?.filter((i) => !i.isDeleted).length ?? 0) > 0;
  const isDataReady = !isProductsLoading && !!categorizedData;

  const isActive = onboarding?.isActive ?? false;
  const pendingItemKeys = onboarding?.pendingItemKeys;
  const setHelpStepOverride = onboarding?.setHelpStepOverride;
  const activateStep = onboarding?.activateStep;

  // Always set help override so "?" button works
  useEffect(() => {
    if (!isDataReady) return;
    setHelpStepOverride?.(hasProducts ? OnboardingItemKey.CashdeskGuide : OnboardingItemKey.CashdeskEmpty);
  }, [isDataReady, hasProducts, setHelpStepOverride]);

  // Auto-activate the right cashdesk guide once per visit while onboarding is active.
  // The page is the sole authority here (the checklist navigates in without activating
  // a step): without products we show ONLY the generic empty-state guide — which has no
  // spotlight target and never completes the "Каса" task. The real tour (CashdeskGuide,
  // which crosses the task off) is shown exclusively when the studio has products.
  useEffect(() => {
    if (!isActive || !isDataReady || hasAutoActivatedRef.current) return;

    if (!hasProducts) {
      hasAutoActivatedRef.current = true;
      activateStep?.(OnboardingItemKey.CashdeskEmpty);
    } else if (pendingItemKeys?.has(OnboardingItemKey.CashdeskGuide)) {
      hasAutoActivatedRef.current = true;
      activateStep?.(OnboardingItemKey.CashdeskGuide);
    }
  }, [isDataReady, hasProducts, isActive, pendingItemKeys, activateStep]);

  useEffect(() => {
    return () => setHelpStepOverride?.(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={styles.root}>
      <nav className={styles.root_nav}>
        <div className={styles.root_nav_title}>Каса</div>
        <div className={styles.root_nav_tabs}>
          <button
            className={classNames(styles.root_nav_tab, { [styles.root_nav_tab_active]: activeTab === 0 })}
            onClick={() => setActiveTab(0)}
          >
            Продажба
          </button>
          <button
            className={classNames(styles.root_nav_tab, { [styles.root_nav_tab_active]: activeTab === 1 })}
            onClick={() => setActiveTab(1)}
            data-onboarding="cashdesk-orders-tab"
          >
            Поръчки
          </button>
        </div>
      </nav>

      {activeTab === 0 && (
        <CashdeskSaleTab
          sale={sale}
          categorizedData={categorizedData}
          isProductsLoading={isProductsLoading}
          studioSettings={studioSettings}
        />
      )}

      {activeTab === 1 && (
        <CashdeskOrdersTab
          orders={orders}
          paymentOptions={sale.paymentOptions}
          studioSettings={studioSettings}
        />
      )}
    </div>
  );
};
